import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const mobileMediaQuery = window.matchMedia('(max-width: 749px)');
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-mobile-marquee')) {
      this.#setupMobileMarquee(signal);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#teardownMobileMarquee();
  }

  #marqueeFrame;
  #marqueeClone;
  #marqueeObserver;
  #marqueeClickTimer;
  #marqueeLastTime = 0;
  #marqueePosition = 0;
  #marqueeVelocity = 0;
  #marqueeBaseSpeed = 12;
  #marqueeIndex = -1;
  #marqueeVisible = true;
  #marqueeDragging = false;
  #marqueeHorizontalDrag = false;
  #marqueePointerId;
  #marqueePointerStartX = 0;
  #marqueePointerStartY = 0;
  #marqueePointerX = 0;
  #marqueePointerTime = 0;
  #marqueeSuppressClick = false;

  #setupMobileMarquee(signal) {
    const scroller = this.querySelector('slideshow-slides');
    if (!scroller) return;

    this.#marqueeBaseSpeed = Math.max(1, Number(this.dataset.mobileMarqueeSpeed) || 12);
    this.#marqueeVelocity = this.#marqueeBaseSpeed;

    mobileMediaQuery.addEventListener('change', this.#updateMarqueePlayback, { signal });
    reducedMotionQuery.addEventListener('change', this.#updateMarqueePlayback, { signal });
    document.addEventListener('visibilitychange', this.#updateMarqueePlayback, { signal });
    scroller.addEventListener('pointerdown', this.#handleMarqueePointerDown, { signal });
    scroller.addEventListener('pointermove', this.#handleMarqueePointerMove, { signal, passive: false });
    window.addEventListener('pointerup', this.#handleMarqueePointerEnd, { signal });
    window.addEventListener('pointercancel', this.#handleMarqueePointerEnd, { signal });
    scroller.addEventListener('click', this.#handleMarqueeClick, { signal, capture: true });

    this.#marqueeObserver = new IntersectionObserver(
      ([entry]) => {
        this.#marqueeVisible = Boolean(entry?.isIntersecting);
        this.#updateMarqueePlayback();
      },
      { rootMargin: '100px 0px', threshold: 0.05 }
    );
    this.#marqueeObserver.observe(this);

    requestAnimationFrame(this.#updateMarqueePlayback);
  }

  #updateMarqueePlayback = () => {
    if (!mobileMediaQuery.matches || reducedMotionQuery.matches) {
      this.#disableMobileMarquee();
      return;
    }

    if (this.#marqueeVisible && !document.hidden) {
      this.#startMobileMarquee();
    } else {
      this.#stopMarqueeFrame();
    }
  };

  #startMobileMarquee() {
    if (this.#marqueeFrame) return;

    const scroller = this.querySelector('slideshow-slides');
    const slides = this.#originalMarqueeSlides;
    if (!scroller || slides.length < 2) return;

    if (!this.#marqueeClone) {
      this.#marqueeClone = /** @type {HTMLElement} */ (slides[0].cloneNode(true));
      this.#marqueeClone.setAttribute('data-marquee-clone', '');
      this.#marqueeClone.setAttribute('aria-hidden', 'false');
      this.#marqueeClone.setAttribute('inert', '');
      this.#marqueeClone.removeAttribute('ref');
      this.#marqueeClone.querySelectorAll('[id], [ref]').forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('ref');
      });
      scroller.append(this.#marqueeClone);
    }

    if (!this.slideshow?.hasAttribute('mobile-marquee')) {
      if (scroller.scrollLeft > 0) this.#marqueePosition = scroller.scrollLeft;
      this.slideshow?.setAttribute('mobile-marquee', '');
      scroller.style.setProperty('--mobile-marquee-offset', `${-this.#marqueePosition}px`);
      scroller.style.setProperty('overflow-x', 'visible');
      scroller.style.setProperty('scroll-snap-type', 'none');
      scroller.style.setProperty('scroll-behavior', 'auto');
      scroller.scrollLeft = 0;
    }

    this.#syncMarqueeControls(this.#marqueePosition, this.#marqueeClone.offsetLeft);
    this.#marqueeLastTime = performance.now();
    this.#marqueeFrame = requestAnimationFrame(this.#advanceMobileMarquee);
  }

  #advanceMobileMarquee = (time) => {
    const scroller = this.querySelector('slideshow-slides');
    const clone = this.#marqueeClone;
    if (!scroller || !clone) {
      this.#stopMarqueeFrame();
      return;
    }

    if (!clone.offsetLeft) {
      this.#marqueeFrame = requestAnimationFrame(this.#advanceMobileMarquee);
      return;
    }

    const elapsed = Math.min(time - this.#marqueeLastTime, 48);
    this.#marqueeLastTime = time;

    if (!this.#marqueeDragging) {
      const settle = 1 - Math.exp(-elapsed / 450);
      this.#marqueeVelocity += (this.#marqueeBaseSpeed - this.#marqueeVelocity) * settle;
      this.#marqueePosition += (this.#marqueeVelocity * elapsed) / 1000;
    } else if (!this.#marqueeHorizontalDrag) {
      this.#marqueeVelocity *= Math.exp(-elapsed / 240);
      this.#marqueePosition += (this.#marqueeVelocity * elapsed) / 1000;
    }

    const cycleWidth = clone.offsetLeft;
    this.#marqueePosition = this.#normalizeMarqueePosition(this.#marqueePosition, cycleWidth);
    scroller.style.setProperty('--mobile-marquee-offset', `${-this.#marqueePosition}px`);

    this.#syncMarqueeControls(this.#marqueePosition, cycleWidth);
    this.#marqueeFrame = requestAnimationFrame(this.#advanceMobileMarquee);
  };

  #syncMarqueeControls(scrollLeft, cycleWidth) {
    const slides = this.#originalMarqueeSlides;
    const slideCount = slides.length;
    const scroller = this.querySelector('slideshow-slides');
    if (!slideCount || !cycleWidth || !scroller) return;

    const viewportCenter = scrollLeft + scroller.clientWidth / 2;
    let index = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    slides.forEach((slide, slideIndex) => {
      const center = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(center - viewportCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        index = slideIndex;
      }
    });

    const cloneCenter = cycleWidth + (slides[0]?.offsetWidth || 0) / 2;
    if (Math.abs(cloneCenter - viewportCenter) < nearestDistance) index = 0;

    this.#marqueeIndex = index;

    this.querySelectorAll('.overlay-dots-nav__dot').forEach((control, controlIndex) => {
      control.setAttribute('aria-selected', controlIndex % slideCount === index ? 'true' : 'false');
    });

    const previousIndex = (index - 1 + slideCount) % slideCount;
    const nextIndex = (index + 1) % slideCount;
    slides.forEach((slide, slideIndex) => {
      const shouldRender = slideIndex === index || slideIndex === previousIndex || slideIndex === nextIndex;
      slide.toggleAttribute('data-marquee-visible', shouldRender);
      slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
    });
  }

  #handleMarqueePointerDown = (event) => {
    if (!mobileMediaQuery.matches || !this.slideshow?.hasAttribute('mobile-marquee')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.#marqueePointerId = event.pointerId;
    this.#marqueePointerStartX = event.clientX;
    this.#marqueePointerStartY = event.clientY;
    this.#marqueePointerX = event.clientX;
    this.#marqueePointerTime = performance.now();
    this.#marqueeDragging = true;
    this.#marqueeHorizontalDrag = false;
    this.#marqueeSuppressClick = false;
  };

  #handleMarqueePointerMove = (event) => {
    if (!this.#marqueeDragging || event.pointerId !== this.#marqueePointerId) return;

    const totalX = event.clientX - this.#marqueePointerStartX;
    const totalY = event.clientY - this.#marqueePointerStartY;
    const deltaX = event.clientX - this.#marqueePointerX;
    const now = performance.now();
    const elapsed = Math.max(8, now - this.#marqueePointerTime);

    this.#marqueePointerX = event.clientX;
    this.#marqueePointerTime = now;

    if (!this.#marqueeHorizontalDrag) {
      if (Math.abs(totalX) < 6 || Math.abs(totalX) <= Math.abs(totalY)) return;

      this.#marqueeHorizontalDrag = true;
      this.slideshow?.setAttribute('marquee-dragging', '');
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    }

    if (event.cancelable) event.preventDefault();

    const resistedDelta = deltaX * 0.88;
    const measuredVelocity = -resistedDelta / (elapsed / 1000);
    this.#marqueePosition -= resistedDelta;
    this.#marqueeVelocity = this.#clampMarqueeVelocity(this.#marqueeVelocity * 0.35 + measuredVelocity * 0.65);
  };

  #handleMarqueePointerEnd = (event) => {
    if (!this.#marqueeDragging || event.pointerId !== this.#marqueePointerId) return;

    if (this.#marqueeHorizontalDrag) {
      this.#marqueeSuppressClick = true;
      clearTimeout(this.#marqueeClickTimer);
      this.#marqueeClickTimer = window.setTimeout(() => {
        this.#marqueeSuppressClick = false;
      }, 350);
    }

    const scroller = this.querySelector('slideshow-slides');
    if (scroller?.hasPointerCapture?.(event.pointerId)) scroller.releasePointerCapture(event.pointerId);

    this.slideshow?.removeAttribute('marquee-dragging');
    this.#marqueeDragging = false;
    this.#marqueeHorizontalDrag = false;
    this.#marqueePointerId = undefined;
  };

  #handleMarqueeClick = (event) => {
    if (!this.#marqueeSuppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#marqueeSuppressClick = false;
  };

  #normalizeMarqueePosition(position, cycleWidth) {
    if (!cycleWidth) return 0;

    return ((position % cycleWidth) + cycleWidth) % cycleWidth;
  }

  #clampMarqueeVelocity(velocity) {
    return Math.min(320, Math.max(-320, velocity));
  }

  #stopMarqueeFrame() {
    cancelAnimationFrame(this.#marqueeFrame);
    this.#marqueeFrame = undefined;
  }

  #teardownMobileMarquee() {
    this.#disableMobileMarquee();
    this.#marqueeObserver?.disconnect();
    this.#marqueeObserver = undefined;
  }

  #disableMobileMarquee() {
    this.#stopMarqueeFrame();
    clearTimeout(this.#marqueeClickTimer);
    this.#marqueeClickTimer = undefined;
    this.#marqueeDragging = false;
    this.#marqueeHorizontalDrag = false;
    this.#marqueePointerId = undefined;
    this.#marqueeSuppressClick = false;
    const scroller = this.querySelector('slideshow-slides');
    if (scroller && this.slideshow?.hasAttribute('mobile-marquee')) {
      this.slideshow.removeAttribute('mobile-marquee');
      this.slideshow.removeAttribute('marquee-dragging');
      scroller.style.removeProperty('--mobile-marquee-offset');
      scroller.style.removeProperty('overflow-x');
      scroller.scrollLeft = 0;
    }
    this.#marqueeIndex = -1;
    this.#marqueePosition = 0;
    this.#marqueeVelocity = this.#marqueeBaseSpeed;
    scroller?.style.removeProperty('scroll-snap-type');
    scroller?.style.removeProperty('scroll-behavior');
    scroller?.style.removeProperty('--mobile-marquee-offset');
    scroller?.style.removeProperty('overflow-x');
    this.#originalMarqueeSlides.forEach((slide, index) => {
      slide.removeAttribute('data-marquee-visible');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
    this.#marqueeClone?.remove();
    this.#marqueeClone = undefined;
  }

  get #originalMarqueeSlides() {
    return [...this.querySelectorAll('slideshow-slides > slideshow-slide:not([data-marquee-clone])')].filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
